import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { MAX_LIST_ROWS } from './helpers';
import {
  FREQUENCY_OPTIONS,
  HOSTING_OPTIONS,
  IP_VERSION_OPTIONS,
  RELATION_OPTIONS,
  SALES_TEAM_OPTIONS,
  TEAM_ACCESS_OPTIONS,
} from './options';

export interface QuestionnaireCompany {
  razaoSocial: string;
  cnpj: string;
}

interface QuestionnaireItem {
  question: string;
  options?: string[];
  hint?: string;
  answer?: string;
}

interface QuestionnaireGroup {
  title: string;
  hint?: string;
  items: QuestionnaireItem[];
}

interface QuestionnaireSection {
  title: string;
  groups: QuestionnaireGroup[];
}

const TITLE = 'Solicitação de acesso à API ConLicitação';
const SUBTITLE = 'Questionário para preenchimento';
const INTRO =
  'Preencha as respostas abaixo e devolva para quem enviou este questionário. ' +
  'As respostas serão lançadas no portal ConLicitação pelo responsável da empresa.';
const FILENAME = 'questionario-api-conlicitacao.docx';

const YES_NO = ['Sim', 'Não'];

const labels = (options: Array<{ label: string }>) => options.map((option) => option.label);

const describedLabels = (options: Array<{ label: string; description: string }>) =>
  options.map((option) => `${option.label} (${option.description})`);

const buildSections = (company: QuestionnaireCompany | null): QuestionnaireSection[] => [
  {
    title: '1. Empresa e solicitante',
    groups: [
      {
        title: 'Empresa contratante',
        items: [
          { question: 'Razão social', answer: company?.razaoSocial },
          { question: 'CNPJ', answer: company?.cnpj },
          {
            question: 'Sua empresa também usa ou já usou o ConLicitação com outros CNPJs?',
            options: YES_NO,
          },
          { question: `Se sim, quais CNPJs? (até ${MAX_LIST_ROWS})` },
        ],
      },
      {
        title: 'Solicitante',
        items: [
          { question: 'Nome completo' },
          { question: 'E-mail corporativo' },
          { question: 'Celular' },
          { question: 'Telefone fixo (opcional)' },
        ],
      },
    ],
  },
  {
    title: '2. Responsável técnico e representante legal',
    groups: [
      {
        title: 'Responsável técnico',
        hint: 'Quem mantém a integração funcionando e será procurado se ela parar.',
        items: [
          { question: 'Nome completo' },
          { question: 'E-mail' },
          { question: 'Celular' },
          { question: 'Vínculo com a empresa', options: labels(RELATION_OPTIONS) },
        ],
      },
      {
        title: 'Representante legal',
        hint: 'Quem assina o contrato e as políticas de uso da API.',
        items: [
          { question: 'Nome completo' },
          { question: 'CPF' },
          {
            question: 'O representante legal trabalha em outras empresas do mercado de licitação?',
            options: YES_NO,
          },
          { question: `Se sim, nome e CNPJ de cada empresa (até ${MAX_LIST_ROWS})` },
        ],
      },
    ],
  },
  {
    title: '3. Operação de vendas e sistema de destino',
    groups: [
      {
        title: 'Operação de vendas',
        items: [
          {
            question: 'Como funciona a sua equipe de vendas ao governo?',
            options: describedLabels(SALES_TEAM_OPTIONS),
          },
          {
            question: 'Como a sua equipe de vendas vai acessar as licitações recebidas pela API?',
            options: describedLabels(TEAM_ACCESS_OPTIONS),
          },
          { question: 'Quantas pessoas trabalham no setor de licitação da sua empresa?' },
          { question: 'Como as licitações recebidas pela API serão usadas na sua empresa?' },
        ],
      },
      {
        title: 'Sistema de destino',
        items: [
          { question: 'Para qual sistema as licitações serão exportadas?' },
          {
            question: 'Nome da aplicação ("motor de busca") que vai receber as licitações da API',
          },
          {
            question: 'Com qual frequência essa aplicação vai receber as licitações?',
            options: labels(FREQUENCY_OPTIONS),
          },
        ],
      },
    ],
  },
  {
    title: '4. IPs e servidores',
    groups: [
      {
        title: 'IPs fixos',
        hint: `Até ${MAX_LIST_ROWS} IPs públicos e fixos que vão acessar a API. Repita este bloco para cada IP.`,
        items: [
          { question: 'Tipo de IP', options: labels(IP_VERSION_OPTIONS) },
          { question: 'Endereço IP' },
          { question: 'Onde fica o servidor com esse IP?', options: labels(HOSTING_OPTIONS) },
          { question: 'Se operadora de internet: CNPJ titular do IP na operadora' },
          {
            question: 'Se operadora de internet: cidade onde o IP está instalado',
            hint: 'Ex.: Campinas/SP',
          },
          {
            question: 'Se servidor em nuvem: provedor de nuvem',
            hint: 'Ex.: AWS, Azure, Google Cloud',
          },
          { question: 'Se servidor em nuvem: região do servidor', hint: 'Ex.: Central US' },
        ],
      },
      {
        title: 'Servidores',
        items: [
          {
            question: 'Em que cidade ou região ficam os servidores que vão buscar as licitações?',
            hint: 'Ex.: São Paulo/SP ou Azure Central US',
          },
        ],
      },
    ],
  },
];

const itemDetail = (item: QuestionnaireItem) => {
  if (item.options) return `Opções: ${item.options.join(' / ')}`;
  return item.hint || '';
};

export const buildQuestionnaireText = (company: QuestionnaireCompany | null) => {
  const lines: string[] = [TITLE.toUpperCase(), SUBTITLE, '', INTRO];

  buildSections(company).forEach((section) => {
    lines.push('', '', section.title.toUpperCase());

    section.groups.forEach((group) => {
      lines.push('', group.title);
      if (group.hint) lines.push(group.hint);

      group.items.forEach((item) => {
        const detail = itemDetail(item);
        lines.push('', `- ${item.question}`);
        if (detail) lines.push(`  ${detail}`);
        lines.push(`  Resposta: ${item.answer || ''}`);
      });
    });
  });

  return lines.join('\n');
};

const paragraph = (text: string, run: { bold?: boolean; size?: number; color?: string } = {}) =>
  new Paragraph({ spacing: { after: 80 }, children: [new TextRun({ text, ...run })] });

const buildQuestionnaireParagraphs = (company: QuestionnaireCompany | null) => {
  const paragraphs: Paragraph[] = [
    paragraph(TITLE, { bold: true, size: 32 }),
    paragraph(SUBTITLE, { size: 24 }),
    paragraph(''),
    paragraph(INTRO),
  ];

  buildSections(company).forEach((section) => {
    paragraphs.push(paragraph(''), paragraph(section.title, { bold: true, size: 28 }));

    section.groups.forEach((group) => {
      paragraphs.push(paragraph(''), paragraph(group.title, { bold: true, size: 24 }));
      if (group.hint) paragraphs.push(paragraph(group.hint, { color: '6C757D' }));

      group.items.forEach((item) => {
        const detail = itemDetail(item);
        paragraphs.push(paragraph(''), paragraph(item.question, { bold: true }));
        if (detail) paragraphs.push(paragraph(detail, { color: '6C757D' }));
        paragraphs.push(paragraph(`Resposta: ${item.answer || ''}`));
      });
    });
  });

  return paragraphs;
};

export const downloadQuestionnaireDocx = async (company: QuestionnaireCompany | null) => {
  const doc = new Document({
    sections: [{ children: buildQuestionnaireParagraphs(company) }],
  });

  saveAs(await Packer.toBlob(doc), FILENAME);
};
